import { Modal } from "react-bootstrap";
import { RiCloseLargeFill } from "react-icons/ri";
import { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import PaymentAccountServices, {
  paymentQrUrl,
} from "../../../services/PaymentAccountServices";

export const PAYMENT_METHODS = [
  { value: "UPI", label: "UPI" },
  { value: "QR", label: "QR Code" },
  { value: "PAYTM", label: "Paytm" },
  { value: "PHONEPE", label: "PhonePe" },
];

// Required fields differ per method, so the schema is built from the method
// the user has currently selected. Mirrors validateAccount() on the server.
const buildValidationSchema = (isEdit) =>
  Yup.object().shape({
    method: Yup.string()
      .oneOf(["UPI", "QR", "PAYTM", "PHONEPE"], "Select a valid method")
      .required("Payment method is required"),
    display_name: Yup.string()
      .trim()
      .max(100, "Display name must be 100 characters or less")
      .required("Display name is required"),
    upi_id: Yup.string().when("method", {
      is: (m) => m === "UPI",
      then: (schema) => schema.trim().required("UPI ID is required"),
      otherwise: (schema) =>
        schema
          .trim()
          .matches(
            /^$|^[a-zA-Z0-9.\-_]{2,}@[a-zA-Z0-9.\-_]{2,}$/,
            "Enter a valid UPI ID (e.g. name@bank)",
          ),
    }),
    mobile_number: Yup.string().when("method", {
      is: (m) => m === "PAYTM" || m === "PHONEPE",
      then: (schema) =>
        schema
          .matches(/^[6-9][0-9]{9}$/, "Enter a valid 10 digit mobile number")
          .required("Mobile number is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    // On edit the existing image counts, so a new file is optional there.
    qr_file: Yup.mixed().when("method", {
      is: "QR",
      then: (schema) =>
        isEdit
          ? schema.notRequired()
          : schema.required("QR image is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    sort_order: Yup.number()
      .typeError("Sort order must be a number")
      .min(0, "Sort order cannot be negative"),
  });

const PaymentAccountModal = (props) => {
  const { modalIsOpen, setIsOpen, editData, getPaymentAccountList } = props;

  const paymentApi = new PaymentAccountServices();
  const isEdit = Boolean(editData?.id);

  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    // Reset the preview whenever the modal is reopened for a different row.
    setPreview(isEdit ? paymentQrUrl(editData?.qr_image) : null);
  }, [modalIsOpen, editData, isEdit]);

  const initialValues = {
    method: editData?.method || "UPI",
    display_name: editData?.display_name || "",
    upi_id: editData?.upi_id || "",
    mobile_number: editData?.mobile_number || "",
    sort_order: editData?.sort_order ?? 0,
    qr_file: null,
  };

  const closeModal = () => {
    setIsOpen(false);
    setPreview(null);
  };

  const handleSubmit = async (values) => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      if (isEdit) formData.append("id", editData.id);
      formData.append("method", values.method);
      formData.append("display_name", values.display_name.trim());
      formData.append("sort_order", values.sort_order || 0);

      if (values.method === "UPI" || values.method === "PAYTM" || values.method === "PHONEPE") {
        if (values.upi_id?.trim()) {
          formData.append("upi_id", values.upi_id.trim());
        }
      }
      if (values.method === "PAYTM" || values.method === "PHONEPE") {
        formData.append("mobile_number", values.mobile_number.trim());
      }
      if (values.method === "QR" && values.qr_file) {
        formData.append("qr_image", values.qr_file);
      }

      const response = isEdit
        ? await paymentApi.updatePaymentAccount(formData)
        : await paymentApi.createPaymentAccount(formData);

      if (response.isSuccess) {
        toast.success(response.message);
        getPaymentAccountList();
        closeModal();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          `Failed to ${isEdit ? "update" : "create"} payment account`,
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      show={modalIsOpen}
      onHide={closeModal}
      backdrop="static"
      className="custom-modal"
      centered
    >
      <Formik
        initialValues={initialValues}
        validationSchema={buildValidationSchema(isEdit)}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <div className="modal-header border-0 pb-0">
              <h5 className="modal-title">
                {isEdit ? "Edit Payment Account" : "Add Payment Account"}
              </h5>
              <button type="button" className="btn" onClick={closeModal}>
                <RiCloseLargeFill size={20} />
              </button>
            </div>
            <hr className="my-1" />

            <Modal.Body>
              <div className="mb-3">
                <label className="form-label">
                  Payment Method <span className="text-danger">*</span>
                </label>
                <Field as="select" name="method" className="form-select">
                  {PAYMENT_METHODS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </Field>
                <ErrorMessage
                  name="method"
                  component="div"
                  className="text-danger small mt-1"
                />
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Display Name <span className="text-danger">*</span>
                </label>
                <Field
                  name="display_name"
                  className="form-control"
                  placeholder="e.g. Primary UPI"
                />
                <ErrorMessage
                  name="display_name"
                  component="div"
                  className="text-danger small mt-1"
                />
              </div>

              {values.method === "UPI" && (
                <div className="mb-3">
                  <label className="form-label">
                    UPI ID <span className="text-danger">*</span>
                  </label>
                  <Field
                    name="upi_id"
                    className="form-control"
                    placeholder="name@bank"
                  />
                  <ErrorMessage
                    name="upi_id"
                    component="div"
                    className="text-danger small mt-1"
                  />
                </div>
              )}

              {(values.method === "PAYTM" || values.method === "PHONEPE") && (
                <>
                  <div className="mb-3">
                    <label className="form-label">
                      Mobile Number <span className="text-danger">*</span>
                    </label>
                    <Field
                      name="mobile_number"
                      className="form-control"
                      placeholder="10 digit mobile number"
                      maxLength={10}
                    />
                    <ErrorMessage
                      name="mobile_number"
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">
                      UPI ID{" "}
                      <span className="text-muted">(optional, for Pay button)</span>
                    </label>
                    <Field
                      name="upi_id"
                      className="form-control"
                      placeholder="e.g. name@oksbi"
                    />
                    <div className="form-text">
                      Add the payee UPI ID so the app opens PhonePe/Paytm pay
                      screen correctly. Without this, players pay manually to
                      the mobile number.
                    </div>
                    <ErrorMessage
                      name="upi_id"
                      component="div"
                      className="text-danger small mt-1"
                    />
                  </div>
                </>
              )}

              {values.method === "QR" && (
                <div className="mb-3">
                  <label className="form-label">
                    QR Image{" "}
                    {!isEdit && <span className="text-danger">*</span>}
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={(e) => {
                      const file = e.currentTarget.files?.[0] || null;
                      setFieldValue("qr_file", file);
                      setPreview(file ? URL.createObjectURL(file) : null);
                    }}
                  />
                  <ErrorMessage
                    name="qr_file"
                    component="div"
                    className="text-danger small mt-1"
                  />
                  {isEdit && (
                    <div className="form-text">
                      Leave empty to keep the current image.
                    </div>
                  )}
                  {preview && (
                    <div className="pa-qr-preview mt-2">
                      <img src={preview} alt="QR preview" />
                    </div>
                  )}
                </div>
              )}

              <div className="mb-1">
                <label className="form-label">Sort Order</label>
                <Field
                  name="sort_order"
                  type="number"
                  min="0"
                  className="form-control"
                />
                <ErrorMessage
                  name="sort_order"
                  component="div"
                  className="text-danger small mt-1"
                />
                <div className="form-text">
                  Lower numbers appear first on the deposit screen.
                </div>
              </div>
            </Modal.Body>

            <div className="modal-footer border-0">
              <button
                type="button"
                className="btn btn-light px-4"
                onClick={closeModal}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary px-4"
                disabled={isLoading}
              >
                {isLoading
                  ? isEdit
                    ? "Updating..."
                    : "Creating..."
                  : isEdit
                    ? "Update"
                    : "Create"}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default PaymentAccountModal;
